import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col, Card, CardBody, Form, FormGroup, Label, Input, Button, InputGroup, InputGroupText, Badge } from "reactstrap";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import Swal from "sweetalert2";
import { toast } from "react-toastify";

const PaymentCreate = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [clients, setClients] = useState([]);
    const [clientSummary, setClientSummary] = useState({
        total_invoice: 0,
        total_paid: 0,
        remaining_balance: 0
    });

    const [formData, setFormData] = useState({
        client_id: "",
        amount: "",
        payment_mode: "cash", // Machan, default value "cash"
        note: ""
    });

    // Machan, payment modes dropdown kaga intha options
    const paymentModeOptions = [
        { value: "cash", label: "💵 Cash Collection" },
        { value: "bank", label: "🏦 Bank Transfer" },
        { value: "upi", label: "📲 UPI (GPay/PhonePe)" },
        { value: "cheque", label: "✍️ Cheque Payment" }
    ];

    useEffect(() => {
        fetchClients();
    }, []);

    const fetchClients = async () => {
        dispatch(loadingTrue());
        try {
            const res = await Handler({ method: "GET", url: "/clients/list.php" });
            if (res?.success) {
                const clientOptions = res.data.map(c => ({
                    value: c.id,
                    label: c.client?.name?.toUpperCase() || "UNKNOWN"
                }));
                setClients(clientOptions);
            }
        } catch (err) {
            toast.error("Error loading clients");
        } finally {
            dispatch(loadingFalse());
        }
    };

    const handleClientChange = async (clientId) => {
        setFormData({ ...formData, client_id: clientId, amount: "" });
        setClientSummary({ total_invoice: 0, total_paid: 0, remaining_balance: 0 });

        if (clientId) {
            dispatch(loadingTrue());
            try {
                // PHP API call to get current balance
                const sumRes = await Handler({ method: "GET", url: `/payments/balance.php?client_id=${clientId}` });
                if (sumRes?.success) {
                    setClientSummary({
                        total_invoice: sumRes.data.summary.total_invoice_amount,
                        total_paid: sumRes.data.summary.total_paid_amount,
                        remaining_balance: sumRes.data.summary.remaining_balance
                    });
                }
            } catch (err) { 
                toast.error("Error fetching balance details");
            } finally { 
                dispatch(loadingFalse()); 
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.client_id || !formData.amount) return toast.error("Fill required fields");

        const enteringAmt = parseFloat(formData.amount);
        const currentBalance = parseFloat(clientSummary.remaining_balance);

        // Extra amount confirm logic
        if (enteringAmt > currentBalance && currentBalance > 0) {
            const extra = enteringAmt - currentBalance;
            const confirmExtra = await Swal.fire({
                title: 'Extra Amount Detected!',
                text: `You are receiving ₹${extra?.toLocaleString()} more than the balance. Save as Advance?`,
                icon: 'info',
                showCancelButton: true,
                confirmButtonText: 'Yes, Save as Advance',
                cancelButtonText: 'Re-check'
            });
            if (!confirmExtra.isConfirmed) return;
        }

        dispatch(loadingTrue());
        try {
            // 🔥 UPI fix: Directly using current formData values
            const payload = {
                ...formData,
                payment_type: 'client' // Specific bill selection thookiyachu, so ithu direct client credit
            };
            
            const res = await Handler({ method: "POST", url: "/payments/create.php", data: payload });
            if (res?.success) {
                await Swal.fire("Success!", "Payment recorded successfully", "success");
                navigate("/payments");
            }
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to save payment");
        } finally { 
            dispatch(loadingFalse()); 
        }
    };

    const blockInvalidChar = (e) => ["-", "e", "E"].includes(e.key) && e.preventDefault();

    return (
        <div className="page-container p-1 bg-light min-vh-100">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h3 className="fw-bold text-theme m-0">Party Payment Receipt</h3>
                <Badge color="success" pill className="px-3 py-2">General Credit</Badge>
            </div>

            <Form onSubmit={handleSubmit}>
                <Row className="g-4">
                    <Col lg={8}>
                        <Card className="border-0 shadow-sm rounded-4 mb-4">
                            <CardBody className="p-4">
                                <h5 className="fw-bold mb-4 border-start border-4 border-primary ps-3">Party & Payment Info</h5>
                                <Row className="g-3">
                                    <Col md={12}>
                                        <FormGroup>
                                            <Label className="small fw-bold text-muted">Select Party <span className="text-danger">*</span></Label>
                                            <Select
                                                options={clients}
                                                onChange={(opt) => handleClientChange(opt?.value)}
                                                placeholder="Search and Select Party..."
                                                styles={{ control: (b) => ({ ...b, minHeight: '52px', borderRadius: '12px' }) }}
                                                isClearable
                                            />
                                        </FormGroup>
                                    </Col>
                                    
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label className="small fw-bold text-success">Receiving Amount <span className="text-danger">*</span></Label>
                                            <InputGroup size="lg">
                                                <InputGroupText className="bg-white border-end-0 text-success fw-bold">₹</InputGroupText>
                                                <Input
                                                    className="border-start-0 fw-bold text-success"
                                                    type="number"
                                                    min="0"
                                                    onKeyDown={blockInvalidChar}
                                                    required
                                                    value={formData.amount}
                                                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                                                    placeholder="0.00"
                                                    style={{ height: '52px', borderRadius: '0 12px 12px 0' }}
                                                />
                                            </InputGroup>
                                        </FormGroup>
                                    </Col>

                                    <Col md={6}>
                                        <FormGroup>
                                            <Label className="small fw-bold text-muted">Payment Method <span className="text-danger">*</span></Label>
                                            <Select
                                                options={paymentModeOptions}
                                                defaultValue={paymentModeOptions[0]} // Cash default
                                                onChange={(opt) => setFormData({ ...formData, payment_mode: opt.value })}
                                                styles={{ control: (b) => ({ ...b, minHeight: '52px', borderRadius: '12px' }) }}
                                            />
                                        </FormGroup>
                                    </Col>

                                    <Col md={12}>
                                        <FormGroup className="mb-0">
                                            <Label className="small fw-bold text-muted">Reference Note</Label>
                                            <Input
                                                type="textarea"
                                                rows="3"
                                                placeholder="Enter transaction ID, Cheque number or any other remarks..."
                                                value={formData.note}
                                                onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                                                style={{ borderRadius: '12px' }}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>
                    </Col>

                    {/* Sidebar Calculation */}
                    <Col lg={4}>
                        <Card className="border-0 shadow-lg rounded-4 sticky-top bg-theme text-white overflow-hidden" style={{ top: '100px' }}>
                            <div className="p-4 bg-dark bg-opacity-25 text-center">
                                <h6 className="opacity-75 text-uppercase small fw-bold mb-1">Total Outstanding</h6>
                                <h2 className="fw-bold mb-0 text-warning">
                                    ₹ {parseFloat(clientSummary.remaining_balance)?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                </h2>
                                {clientSummary.remaining_balance < 0 && <Badge color="info" className="mt-2">Party is in Advance</Badge>}
                            </div>
                            <CardBody className="p-4">
                                <div className="mb-4">
                                    <div className="d-flex justify-content-between mb-2 small text-white-50">
                                        <span>Total Business:</span>
                                        <span className="fw-bold text-white">₹ {parseFloat(clientSummary.total_invoice)?.toLocaleString()}</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-2 small text-white-50">
                                        <span>Total Paid:</span>
                                        <span className="fw-bold text-info">₹ {parseFloat(clientSummary.total_paid)?.toLocaleString()}</span>
                                    </div>
                                    <hr className="my-3 border-white opacity-10" />
                                    
                                    <div className="bg-white bg-opacity-10 p-3 rounded-4">
                                        <div className="d-flex justify-content-between mb-1 small text-white-50">
                                            <span>Collection Amount:</span>
                                            <span className="fw-bold text-warning">₹ {Number(formData.amount || 0)?.toLocaleString()}</span>
                                        </div>
                                        <div className="d-flex justify-content-between fs-6 fw-bold mt-2">
                                            <span>Balance After:</span>
                                            <span className={ (clientSummary.remaining_balance - (formData.amount || 0)) < 0 ? "text-info" : "text-white"}>
                                                ₹ {(clientSummary.remaining_balance - (formData.amount || 0))?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <Button
                                    color="warning"
                                    block
                                    className="py-3 fw-bold shadow w-100 rounded-3 text-uppercase text-dark border-0"
                                    type="submit"
                                    disabled={!formData.client_id || !formData.amount}
                                >
                                    Confirm Collection
                                </Button>
                                <p className="text-center x-small text-white-50 mt-3 mb-0 italic">
                                    * Entry will be added to the party ledger immediately.
                                </p>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Form>
        </div>
    );
};

export default PaymentCreate;